<template>
    <div class="card shadow-sm h-auto permission-card">
        <div>
            <!-- Collapsible Header -->
            <div 
                :class="[
                    'permission-header d-flex justify-content-between align-items-center cursor-pointer',
                    { 'header-collapsed': isCollapsed }
                ]"
                @click="toggleCollapse"
            >
                <h5 class="mb-0 d-flex align-items-center">
                    <!-- Arrow Icon -->
                    <i 
                        :class="[
                            'fas transition-transform me-2',
                            isCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'
                        ]"
                        style="font-size: 0.8rem; transition: transform 0.2s ease;"
                    ></i>
                    <strong>{{ cleanTitle }}</strong>
                </h5>
                <label 
                    class="select-all-label"
                    @click.stop
                > 
                    <input 
                        :disabled="!allowSelect" 
                        type="checkbox" 
                        :checked="areAllPermissionsSelected"
                        @change="handleSelectAllChange"
                    /> 
                    Select All 
                </label>
            </div>
            
            <!-- Collapsible Content -->
            <div 
                v-show="!isCollapsed"
                class="permission-content"
                style="transition: all 0.3s ease;"
            >
                <hr class="my-3" />
                <label
                    v-if="permissionsGroup && permissionsGroup.permissions"
                    v-for="permission of permissionsGroup.permissions"
                    :key="permission.id"
                    :class="[
                        'd-flex justify-content-between align-items-center my-2 permission-item',
                        {
                            'selected-checkbox-label': selectedPermissionIds.includes(permission.id),
                        },
                    ]"
                >
                    <div class="permission-name">
                        {{ cleanPermissionName(permission.name) }}
                    </div>
                    <input
                        :disabled="!allowSelect"
                        @click="$emit('handlePermissionAdd', { id: permission.id })"
                        :checked="selectedPermissionIds.includes(permission.id)"
                        type="checkbox"
                        class="form-check-input"
                    />
                </label>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return { 
            isCollapsed: false // Start expanded by default
        }
    },
    props: {
        selectedPermissionIds: {
            type: Array,
            default: [],
        },
        permissionsGroup: {
            type: Object,
            default: null,
        },
        allowSelect: {
            type: Boolean,
            default: false,
        },
        initiallyCollapsed: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        /**
         * Clean up the permission group title to be more user-friendly
         */
        cleanTitle() {
            if (!this.permissionsGroup || !this.permissionsGroup.title) return ''
            
            // Convert from "CREW_MANAGEMENT" to "Crew Management"  
            return this.permissionsGroup.title
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
        },
        /**
         * Check if all permissions in this group are selected
         */
        areAllPermissionsSelected() {
            if (!this.permissionsGroup || !this.permissionsGroup.permissions || this.permissionsGroup.permissions.length === 0) return false
            
            return this.permissionsGroup.permissions.every(permission => 
                this.selectedPermissionIds.includes(permission.id)
            )
        }
    },
    mounted() {
        // Set initial collapse state based on prop
        this.isCollapsed = this.initiallyCollapsed
    },
    methods: {
        /**
         * Toggle the collapse state of this permission group
         */
        toggleCollapse() {
            this.isCollapsed = !this.isCollapsed
        },
        
        /**
         * Handle select all checkbox change
         */
        handleSelectAllChange(event) {
            const checked = event.target.checked
            
            if (this.permissionsGroup && this.permissionsGroup.permissions) {
                for (let permission of this.permissionsGroup.permissions) {
                    if (checked) {
                        this.$emit("handlePermissionAdd", { id: permission.id, forceRemove: false })
                    } else {
                        this.$emit("handlePermissionAdd", { id: permission.id, forceRemove: true })
                    }
                }
            }
        },
        
        /**
         * Clean up permission names to be more user-friendly
         */
        cleanPermissionName(name) {
            if (!name) return ''
            
            // Handle special cases first
            const specialCases = {
                'View Trigger password reset': 'Trigger Password Reset',
                'Customer Import': 'Import Customers',
                'Product Import': 'Import Products',
                'Print Label': 'Print Product Labels',
                'ASN Stock Import': 'Import ASN Stock',
                'COVA Stock Import': 'Import COVA Stock'
            }
            
            // Check if this is already a special case (like "View Trigger password reset")
            if (specialCases[name]) {
                return specialCases[name]
            }
            
            // Remove "CMS_" prefix and clean up the rest
            let cleaned = name.replace(/^CMS_/, '').replace(/^POS_/, '')
            
            // Split by underscores and clean up each part
            const parts = cleaned.split('_')
            const action = parts[0] // CREATE, VIEW, EDIT, DELETE
            const subject = parts.slice(1).join(' ') // CREW MANAGEMENT -> Crew Management
            
            // Convert action to more friendly text
            const actionMap = {
                'CREATE': 'Create',
                'VIEW': 'View', 
                'EDIT': 'Edit',
                'DELETE': 'Delete'
            }
            
            const friendlyAction = actionMap[action] || action
            
            // Convert subject to title case and handle special terms
            const subjectMap = {
                'CREW MANAGEMENT': 'Staff',
                'CREW ROLE': 'Staff Roles',
                'CUSTOMER GOVT ID': 'Customer Government ID',
                'CUSTOMER GROUP': 'Customer Groups',
                'INVENTORY MANAGER': 'Inventory',
                'PAYMENT SETUP': 'Payment Methods',
                'GL ACCOUNT': 'General Ledger Accounts',
                'GL DEFAULT': 'GL Account Defaults',
                'AGCO REPORTING': 'AGCO Reports',
                'AGCO LOG': 'AGCO Logs'
            }
            
            const friendlySubject = subjectMap[subject] || subject
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            
            return `${friendlyAction} ${friendlySubject}`
        }
    },
}
</script>

<style scoped>
/* Collapsible permission card styling */
.permission-header {
    padding: 0.75rem;
    transition: all 0.2s ease;
    margin: 0;
}

/* When expanded - only top corners rounded */
.permission-header {
    border-radius: 0.375rem 0.375rem 0 0;
}

/* When collapsed - all corners rounded */
.permission-header.header-collapsed {
    border-radius: 0.375rem;
}

.permission-header:hover {
    background-color: rgba(13, 45, 44, 0.08);
    box-shadow: inset 0 0 0 1px rgba(13, 45, 44, 0.2);
    border-left: 3px solid rgba(13, 45, 44, 0.6);
}

.cursor-pointer {
    cursor: pointer;
}

.transition-transform {
    transition: transform 0.2s ease;
}

.permission-content {
    animation: slideDown 0.3s ease;
    padding: 0 0.75rem 0.75rem 0.75rem;
}

@keyframes slideDown {
    from {
        opacity: 0;
        max-height: 0;
    }
    to {
        opacity: 1;
        max-height: 500px;
    }
}

.permission-item {
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
    margin: 0.1rem 0;
}

.permission-item:hover {
    background-color: rgba(13, 45, 44, 0.05);
    transform: translateX(2px);
}

.permission-name {
    font-size: 0.9rem;
    color: #495057;
}

.selected-checkbox-label {
    font-weight: 600;
    background-color: rgba(13, 45, 44, 0.1);
}

.selected-checkbox-label .permission-name {
    color: #0D2D2C;
}

.select-all-label {
    font-size: 0.85rem;
    color: #495057;
    margin-bottom: 0;
    padding: 0.375rem 0.625rem;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.15);
    font-weight: 500;
}

.select-all-label:hover {
    background-color: rgba(13, 45, 44, 0.1);
    border-color: rgba(13, 45, 44, 0.4);
    color: #0D2D2C;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(13, 45, 44, 0.2);
}

.select-all-label input {
    margin-right: 0.5rem;
}

/* Card styling */
.permission-card {
    padding: 0;
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.125);
    min-height: 80px; /* Prevent shrinking */
    border-radius: 0.375rem;
    overflow: hidden; /* Ensure content respects border radius */
}

.permission-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(13, 45, 44, 0.25);
}

.h-auto {
    height: auto !important;
}

/* Form check input styling */
.form-check-input {
    margin-top: 0;
    margin-left: 0.5rem;
}

/* Responsive improvements */
@media (max-width: 768px) {
    .permission-header h5 {
        font-size: 1rem;
    }
    
    .permission-name {
        font-size: 0.85rem;
    }
}
</style>
